import React from 'react'
import { Text, TextStyle, View } from 'react-native'
import { RawDraftContentBlock, RawDraftContentState } from 'draft-js'
import { colors, typography } from '../tokens'
import { useDocletLayout } from '../DocletLayoutContext'
import { segmentBlock } from './segments'

type EntityMap = RawDraftContentState['entityMap']

const LIST_TYPES = ['unordered-list-item', 'ordered-list-item']

/** The text style for a Draft.js block type, from the doclet tokens. */
export const blockTextStyle = (type: string, isWide: boolean): TextStyle => {
  switch (type) {
    case 'heading':
    case 'header-one':
    case 'header-two':
      return typography.heading(isWide)
    case 'sub-heading':
    case 'header-three':
    case 'header-four':
      return typography.subheading(isWide)
    case 'small':
      return typography.small(isWide)
    default:
      return typography.body(isWide)
  }
}

const HEADING_TYPES = [
  'heading',
  'sub-heading',
  'header-one',
  'header-two',
  'header-three',
  'header-four'
]

// Legacy bold switches body text to the Baskerville bold face; that face would
// be wrong in an Akzidenz heading, which just gets heavier instead.
const boldStyle = (blockType: string): TextStyle =>
  HEADING_TYPES.includes(blockType) ? { fontWeight: '700' } : typography.bold

const Segments = ({
  block,
  entityMap
}: {
  block: RawDraftContentBlock
  entityMap: EntityMap
}) => (
  <>
    {segmentBlock(block, entityMap).map((segment, i) => {
      const style: TextStyle = {
        ...(segment.bold ? boldStyle(block.type) : {}),
        ...(segment.italic ? { fontStyle: 'italic' } : {}),
        ...(segment.url ? { color: colors.link } : {})
      }
      if (segment.url) {
        return (
          <Text
            key={i}
            style={style}
            accessibilityRole="link"
            // react-native-web renders these as <a href target=_blank>
            {...({
              href: segment.url,
              hrefAttrs: { target: '_blank', rel: 'noopener noreferrer' }
            } as any)}
          >
            {segment.text}
          </Text>
        )
      }
      return (
        <Text key={i} style={style}>
          {segment.text}
        </Text>
      )
    })}
    {/* Keeps an empty paragraph one line tall, as in the editor */}
    {!block.text && ' '}
  </>
)

type Group =
  | { kind: 'block'; block: RawDraftContentBlock }
  | { kind: 'list'; ordered: boolean; items: RawDraftContentBlock[] }

const groupLists = (blocks: RawDraftContentBlock[]): Group[] =>
  blocks.reduce<Group[]>((groups, block) => {
    if (block.type === 'atomic') return groups // inline media: not supported yet
    if (LIST_TYPES.includes(block.type)) {
      const ordered = block.type === 'ordered-list-item'
      const last = groups[groups.length - 1]
      if (last?.kind === 'list' && last.ordered === ordered) {
        last.items.push(block)
      } else {
        groups.push({ kind: 'list', ordered, items: [block] })
      }
    } else {
      groups.push({ kind: 'block', block })
    }
    return groups
  }, [])

interface RichTextViewProps {
  content: RawDraftContentState
}

/**
 * Read-only rendering of a Text block's Draft.js content with React Native
 * primitives, so it works in MI6's preview and, later, on the share site. The
 * builder edits the same content with Draft.js's own editor, styled to match
 * (see TextBlockEditor).
 */
const RichTextView = ({ content }: RichTextViewProps) => {
  const { isWide } = useDocletLayout()
  const body = typography.body(isWide)
  const list = typography.list(isWide)

  return (
    <View>
      {groupLists(content.blocks).map((group, groupIndex) => {
        if (group.kind === 'list') {
          return (
            <View
              key={group.items[0].key}
              style={{ marginBottom: body.marginBottom }}
            >
              {group.items.map((item, i) => (
                <View
                  key={item.key}
                  style={{
                    flexDirection: 'row',
                    marginTop: i === 0 ? 0 : list.itemSpacing,
                    paddingLeft: item.depth * list.indent
                  }}
                >
                  <Text
                    style={[
                      body,
                      {
                        width: list.indent,
                        marginBottom: 0,
                        textAlign: 'center'
                      }
                    ]}
                  >
                    {group.ordered ? `${i + 1}.` : '•'}
                  </Text>
                  <Text style={[body, { flex: 1, marginBottom: 0 }]}>
                    <Segments block={item} entityMap={content.entityMap} />
                  </Text>
                </View>
              ))}
            </View>
          )
        }

        const { block } = group
        const style = blockTextStyle(block.type, isWide)
        if (block.type === 'blockquote') {
          return (
            <View
              key={block.key || groupIndex}
              style={{
                borderLeftWidth: 2,
                borderLeftColor: colors.rule,
                paddingLeft: body.fontSize,
                marginBottom: body.marginBottom
              }}
            >
              <Text style={[style, { marginBottom: 0 }]}>
                <Segments block={block} entityMap={content.entityMap} />
              </Text>
            </View>
          )
        }
        // Every block fills the page column, which sets the reading width
        return (
          <Text key={block.key || groupIndex} style={style}>
            <Segments block={block} entityMap={content.entityMap} />
          </Text>
        )
      })}
    </View>
  )
}

export default RichTextView
